import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { PageLoadingSkeleton } from '../components/PageLoadingSkeleton';

function formatOrderStatus(status) {
  return String(status || '')
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatTimestamp(value) {
  if (!value) {
    return 'No messages yet';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'No messages yet';
  }

  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function MessagesPage({ currentUser, snapshot, loading }) {
  const [selectedThreadId, setSelectedThreadId] = useState('');

  const threads = useMemo(() => {
    if (!snapshot?.orders) {
      return [];
    }

    return [...snapshot.orders]
      .map((order) => {
        const latestMessage = [...(order.messages || [])].sort(
          (left, right) => new Date(right.createdAt) - new Date(left.createdAt)
        )[0];

        return {
          id: order.id,
          order,
          listingTitle: order.listing?.title || order.listingTitle || 'Untitled agent',
          counterpartyName: order.counterparty?.name || 'Marketplace user',
          status: order.status,
          latestMessage,
        };
      })
      .sort((left, right) => {
        const leftTime = left.latestMessage?.createdAt ? new Date(left.latestMessage.createdAt).getTime() : 0;
        const rightTime = right.latestMessage?.createdAt ? new Date(right.latestMessage.createdAt).getTime() : 0;
        return rightTime - leftTime;
      });
  }, [snapshot]);

  const selectedThread = useMemo(() => {
    return threads.find((thread) => thread.id === selectedThreadId) || threads[0] || null;
  }, [selectedThreadId, threads]);

  useEffect(() => {
    if (!selectedThreadId && threads.length > 0) {
      setSelectedThreadId(threads[0].id);
      return;
    }

    if (selectedThreadId && !threads.some((thread) => thread.id === selectedThreadId)) {
      setSelectedThreadId(threads[0]?.id || '');
    }
  }, [selectedThreadId, threads]);

  if (!currentUser) {
    return <Navigate replace to="/login" />;
  }

  if (loading || !snapshot) {
    return <PageLoadingSkeleton label="Loading messages" variant="compact" />;
  }

  return (
    <section className="page-shell account-page">
      <div className="page-head account-head">
        <div>
          <span className="eyebrow left">Messages</span>
          <h1>Your conversations</h1>
          <p>Access all buyer and seller order conversations in one place.</p>
        </div>
      </div>

      {threads.length === 0 ? (
        <div className="detail-panel">
          <p>No message threads yet. Once an order has conversation activity, it will appear here.</p>
        </div>
      ) : (
        <div className="messages-layout">
          <aside className="detail-panel messages-sidebar">
            <div className="messages-sidebar-head">
              <h2>Conversations</h2>
              <span>{threads.length}</span>
            </div>

            <div className="message-thread-list">
              {threads.map((thread) => (
                <button
                  key={thread.id}
                  type="button"
                  className={`message-thread-card${selectedThread?.id === thread.id ? ' active' : ''}`}
                  onClick={() => setSelectedThreadId(thread.id)}
                >
                  <div className="message-thread-top">
                    <strong>{thread.counterpartyName}</strong>
                    <span>{formatTimestamp(thread.latestMessage?.createdAt)}</span>
                  </div>
                  <h3>{thread.listingTitle}</h3>
                  <p>{thread.latestMessage?.body || 'Open this order to start the conversation.'}</p>
                </button>
              ))}
            </div>
          </aside>

          <section className="detail-panel messages-main">
            {selectedThread ? (
              <>
                <div className="messages-main-head">
                  <div>
                    <h2>{selectedThread.counterpartyName}</h2>
                    <p>{selectedThread.listingTitle}</p>
                  </div>
                  <div className="messages-main-meta">
                    <span className={`account-order-status status-${selectedThread.status}`}>
                      {formatOrderStatus(selectedThread.status)}
                    </span>
                    <Link to={`/orders/${selectedThread.id}`} className="ghost-button ui-button">
                      Open order
                    </Link>
                  </div>
                </div>

                {selectedThread.order.messages?.length > 0 ? (
                  <div className="order-message-list messages-conversation-list">
                    {selectedThread.order.messages.map((message) => (
                      <article
                        key={message.id}
                        className={`order-message-item${message.senderId === currentUser.id ? ' is-current-user' : ''}`}
                      >
                        <div className="order-message-top">
                          <strong>{message.senderId === currentUser.id ? 'You' : formatOrderStatus(message.senderRole)}</strong>
                          <span>{formatTimestamp(message.createdAt)}</span>
                        </div>
                        <p>{message.body}</p>
                      </article>
                    ))}
                  </div>
                ) : (
                  <p>No messages yet. Open the order workspace to start the conversation.</p>
                )}
              </>
            ) : null}
          </section>
        </div>
      )}
    </section>
  );
}
